import { IClinicNumber, IWorkingHours } from '@dataTypes/clinic.type'

export const DAY_ORDER: IWorkingHours['day_of_week'][] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

const WEEKDAYS = DAY_ORDER.slice(0, 5)

function toAmPm(hms: string): string {
  const p = hms.slice(0, 5).split(':')
  if (p.length < 2) return hms
  let h = parseInt(p[0], 10)
  const m = p[1]
  const ap = h >= 12 ? 'PM' : 'AM'
  if (h === 0) h = 12
  else if (h > 12) h -= 12
  return `${h}:${m} ${ap}`
}

export function formatHmsAsAmPm(hms: string | undefined): string {
  const s = (hms || '08:00:00').trim()
  if (s.length < 5) return '8:00 AM'
  return toAmPm(s)
}

export function amPmLabelToHms(label: string): string {
  const t = label.trim()
  const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(t)
  if (!match) return '08:00:00'
  let h = parseInt(match[1], 10)
  const m = match[2]
  const ap = match[3].toUpperCase()
  if (h < 1 || h > 12) return `08:${m}:00`
  if (ap === 'AM') {
    if (h === 12) h = 0
  } else {
    if (h !== 12) h += 12
  }
  return `${String(h).padStart(2, '0')}:${m}:00`
}

export function buildTimeOptionsAmPm(stepMinutes = 30): string[] {
  const out: string[] = []
  for (let hour = 0; hour < 24; hour++) {
    for (let m = 0; m < 60; m += stepMinutes) {
      const hh = String(hour).padStart(2, '0')
      const mm = String(m).padStart(2, '0')
      out.push(toAmPm(`${hh}:${mm}`))
    }
  }
  return out
}

export function formatHoursAmPm(wh?: IWorkingHours[]): string {
  if (!wh || wh.length === 0) return '—'
  const first = wh.find((x) => x.day_of_week === 'Monday') || wh[0]
  const start = toAmPm(first.start_time || '')
  const end = toAmPm(first.end_time || '')
  return start && end ? `${start} -> ${end}` : '—'
}

export function buildTimeOptions(stepMinutes = 30): string[] {
  const out: string[] = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += stepMinutes) {
      const hh = String(h).padStart(2, '0')
      const mm = String(m).padStart(2, '0')
      out.push(`${hh}:${mm}`)
    }
  }
  return out
}

/** When "same working hours" UI is on: expand weekday row + Sat + Sun into Mon–Sun records for API. */
export function expandCompactWorkingHours(
  weekdayOn: boolean,
  weekdayStart: string,
  weekdayEnd: string,
  satOn: boolean,
  satStart: string,
  satEnd: string,
  sunOn: boolean,
  sunStart: string,
  sunEnd: string,
): Array<Pick<IWorkingHours, 'day_of_week' | 'start_time' | 'end_time'>> {
  const rows: Array<
    Pick<IWorkingHours, 'day_of_week' | 'start_time' | 'end_time'>
  > = []
  if (weekdayOn) {
    WEEKDAYS.forEach((d) =>
      rows.push({
        day_of_week: d,
        start_time: weekdayStart,
        end_time: weekdayEnd,
      }),
    )
  }
  if (satOn)
    rows.push({
      day_of_week: 'Saturday',
      start_time: satStart,
      end_time: satEnd,
    })
  if (sunOn)
    rows.push({
      day_of_week: 'Sunday',
      start_time: sunStart,
      end_time: sunEnd,
    })
  return rows
}

export const safeParseMethods = (s?: string | null): string[] => {
  if (!s) return []
  try {
    const v = JSON.parse(s)
    return Array.isArray(v) ? v.map(String) : []
  } catch {
    return []
  }
}

export const parseHmsToSec = (hms: string): number | null => {
  const s = (hms || '').trim()
  if (!s) return null
  const parts = s.split(':').map((x) => parseInt(x, 10))
  if (parts.some((n) => Number.isNaN(n))) return null
  const h = parts[0] ?? 0
  const m = parts[1] ?? 0
  const sec = parts[2] ?? 0
  return h * 3600 + m * 60 + sec
}

export function clinicNumberHasWorkingHoursSetup(
  n: IClinicNumber | null | undefined,
): boolean {
  if (!n?.clinic_number_id) return false
  return Array.isArray(n.workingHours) && n.workingHours.length > 0
}

const whForCompare = (
  wh: IWorkingHours,
): Pick<
  IWorkingHours,
  'day_of_week' | 'start_time' | 'end_time' | 'name' | 'timezone'
> => ({
  day_of_week: wh.day_of_week,
  start_time: wh.start_time,
  end_time: wh.end_time,
  name: wh.name,
  timezone: wh.timezone,
})

function normalizeWorkingHoursList(list: IWorkingHours[] | undefined): string {
  if (!list?.length) return '[]'
  return JSON.stringify(
    [...list]
      .map(whForCompare)
      .sort((a, b) =>
        String(a.day_of_week).localeCompare(String(b.day_of_week)),
      ),
  )
}

/**
 * True when the working-hours–related fields match (vs clinic store baseline).
 * Used to detect local edits in the call-flow working hours panel.
 */
export function clinicWorkingHoursDataEqual(
  a: IClinicNumber,
  b: IClinicNumber,
): boolean {
  if ((a.same_working_hours ?? null) !== (b.same_working_hours ?? null)) {
    return false
  }
  if ((a.apply_working_hours ?? null) !== (b.apply_working_hours ?? null)) {
    return false
  }
  if (
    normalizeWorkingHoursList(a.workingHours) !==
    normalizeWorkingHoursList(b.workingHours)
  ) {
    return false
  }
  return true
}
